import { useTranslation } from 'react-i18next';
import { SESSION_RATING_LEVELS, type SessionRating } from '@repo/shared-types';
import { cn } from '@repo/ui/src/lib/utils';

/**
 * THI-346: the five-level picker on a student's personal session note.
 * Best level on the right so the row reads like a scale. Five toggle
 * buttons in a group (`aria-pressed`), not a radiogroup: the level is
 * optional at every layer, and "tap the pressed one again to clear" is
 * honest toggle behaviour where a radio would lie to a screen reader. The
 * emoji is large on purpose: the teacher who asked for this reads the face
 * first. The line under the row names the chosen level and offers "Bỏ chọn".
 */
export function SessionRatingPicker({
  value,
  onChange,
}: {
  value: SessionRating | null;
  onChange: (next: SessionRating | null) => void;
}) {
  const { t } = useTranslation('progress');
  const levels = [...SESSION_RATING_LEVELS].reverse();
  const current = levels.find((l) => l.level === value) ?? null;
  return (
    <div>
      <div role="group" aria-label={t('notes.student.rating')} className="flex gap-1.5">
        {levels.map((l) => {
          const selected = l.level === value;
          return (
            <button
              key={l.level}
              type="button"
              aria-pressed={selected}
              aria-label={l.label}
              onClick={() => onChange(selected ? null : l.level)}
              className={cn(
                'flex h-[44px] flex-1 items-center justify-center rounded-md border text-[24px] leading-none transition-colors',
                selected
                  ? 'border-brand-500 bg-brand-50 shadow-[inset_0_0_0_1px_var(--color-brand-500)]'
                  : 'border-surface-200 bg-surface-0 grayscale-[35%] hover:grayscale-0',
              )}
            >
              <span aria-hidden="true">{l.emoji}</span>
            </button>
          );
        })}
      </div>
      <p className={cn('mt-1.5 flex min-h-[18px] items-center gap-2 text-[12.5px]', current ? 'font-medium text-surface-800' : 'text-surface-400')}>
        {current ? (
          <>
            <span><span aria-hidden="true">{current.emoji} </span>{current.label}</span>
            <button type="button" onClick={() => onChange(null)} className="font-normal text-surface-500 underline-offset-2 hover:underline">
              {t('notes.student.ratingClear')}
            </button>
          </>
        ) : (
          t('notes.student.ratingHint')
        )}
      </p>
    </div>
  );
}
